import { useEffect, useState } from 'react';
import { catalogService } from '../../../services/catalogService';
import type { CatalogView } from '../../../types/catalog';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

interface UseCatalogViewDataParams {
  catalogId: number;
}

export function useCatalogViewData({ catalogId }: UseCatalogViewDataParams) {
  const [catalog, setCatalog] = useState<CatalogView | null>(null);
  // Only the very first load blanks the screen; later page/search loads keep the list visible
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(1);

  // Search
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch current page
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setFetching(true);
      try {
        const response = await catalogService.getView(catalogId, {
          search: debouncedSearch || undefined,
          page: page - 1,
          size: PAGE_SIZE,
        });
        if (cancelled) return;
        setCatalog(response);
        setError(null);
      } catch (e) {
        if (cancelled) return;
        // 401 is handled globally (redirect to login, then back here)
        const status = (e as { response?: { status?: number } }).response?.status;
        if (status === 404) {
          setNotFound(true);
        } else if (status !== 401) {
          setError('Не вдалося завантажити каталог');
        }
      } finally {
        if (!cancelled) {
          setFetching(false);
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [catalogId, page, debouncedSearch]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({ top: 0 });
  };

  const handleClearSearch = () => {
    setSearch('');
  };

  return {
    data: {
      catalog,
      loading,
      fetching,
      notFound,
      error,
    },
    pagination: {
      page,
      count: catalog?.totalPages ?? 0,
      onChange: handlePageChange,
    },
    filters: {
      search,
      setSearch,
      debouncedSearch,
      handleClearSearch,
    },
  };
}
